import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import RouteSEO from "./components/RouteSEO.tsx";
import { CATALOG_URL } from "@/lib/catalog";

const PreferredSourcesPrompt = lazy(() => import("./components/PreferredSourcesPrompt.tsx"));


const DeferredOverlays = lazy(() => import("./components/DeferredOverlays.tsx"));

const importProductPage = () => import("./pages/ProductPage.tsx");
const importAccessibilityStatement = () => import("./pages/AccessibilityStatement.tsx");
const importPrivacyPolicy = () => import("./pages/PrivacyPolicy.tsx");
const importTermsOfService = () => import("./pages/TermsOfService.tsx");
const importNotFound = () => import("./pages/NotFound.tsx");
const importRecipes = () => import("./pages/Recipes.tsx");
const importRecipePage = () => import("./pages/RecipePage.tsx");
const importAdmin = () => import("./pages/Admin.tsx");
const importCategoryPage = () => import("./pages/CategoryPage.tsx");
const importAddRecipe = () => import("./pages/AddRecipe.tsx");
const importAddProduct = () => import("./pages/AddProduct.tsx");
const importSitemap = () => import("./pages/Sitemap.tsx");
const importCustomerLanding = () => import("./pages/CustomerLanding.tsx");
const importStoreLocator = () => import("./pages/StoreLocator.tsx");
const importLegacyRedirects = () => import("./components/LegacyRedirects.tsx");

const ProductPage = lazy(importProductPage);
const AccessibilityStatement = lazy(importAccessibilityStatement);
const PrivacyPolicy = lazy(importPrivacyPolicy);
const TermsOfService = lazy(importTermsOfService);
const NotFound = lazy(importNotFound);
const Recipes = lazy(importRecipes);
const RecipePage = lazy(importRecipePage);
const Admin = lazy(importAdmin);
const CategoryPage = lazy(importCategoryPage);
const AddRecipe = lazy(importAddRecipe);
const AddProduct = lazy(importAddProduct);
const Sitemap = lazy(importSitemap);
const CustomerLanding = lazy(importCustomerLanding);
const StoreLocator = lazy(importStoreLocator);
const ProductLegacyRedirect = lazy(() =>
  importLegacyRedirects().then((m) => ({ default: m.ProductLegacyRedirect })),
);
const RecipeLegacyRedirect = lazy(() =>
  importLegacyRedirects().then((m) => ({ default: m.RecipeLegacyRedirect })),
);

// Prefetch likely-next routes only after first user interaction so they
// don't appear in the initial critical request chain.
const PrefetchRoutes = () => {
  useEffect(() => {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      cleanup();
      const idle =
        (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ||
        ((cb: () => void) => setTimeout(cb, 2000));
      idle(() => {
        importCategoryPage();
        importProductPage();
        importRecipes();
        importRecipePage();
        importStoreLocator();
      });
    };
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll", "touchstart"];
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, run));
    events.forEach((e) => window.addEventListener(e, run, { once: true, passive: true }));
    const fallback = setTimeout(run, 8000);
    return () => {
      cleanup();
      clearTimeout(fallback);
    };
  }, []);
  return null;
};

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 rounded-full border-4 border-muted border-t-pl-blue animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const useDeferredMount = (delay = 1500) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const idle =
      (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ||
      ((cb: () => void) => setTimeout(cb, delay));
    idle(() => setReady(true));
  }, [delay]);
  return ready;
};

const Overlays = () => {
  const ready = useDeferredMount();
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <DeferredOverlays />
    </Suspense>
  );
};

/** Keeps the historic /pastaluna-catalog.pdf link working by sending it to the catalog in storage. */
const CatalogRedirect = () => {
  useEffect(() => {
    window.location.replace(CATALOG_URL);
  }, []);
  return <RouteFallback />;
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RouteSEO />
        <PrefetchRoutes />
        <Suspense fallback={null}>
          <PreferredSourcesPrompt />
        </Suspense>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/pastaluna-catalog.pdf" element={<CatalogRedirect />} />
            <Route path="/catalog" element={<CatalogRedirect />} />

            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/business" element={<CustomerLanding type="business" />} />
            <Route path="/private" element={<CustomerLanding type="private" />} />
            <Route path="/where-to-buy" element={<StoreLocator />} />
            <Route path="/wholesale" element={<Navigate to="/business" replace />} />
            <Route path="/retail" element={<Navigate to="/private" replace />} />

            <Route path="/product/:category/:id" element={<ProductPage />} />
            <Route path="/product/:id" element={<ProductLegacyRedirect />} />
            <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
            <Route path="/accessibility" element={<Navigate to="/accessibility-statement" replace />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/recipes/:series/:id" element={<RecipePage />} />
            <Route path="/recipes/:id" element={<RecipeLegacyRedirect />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/yonatan" element={<Admin />} />
            <Route path="/yonatan/add-recipe" element={<AddRecipe />} />
            <Route path="/yonatan/edit-recipe/:id" element={<AddRecipe />} />
            <Route path="/yonatan/add-product" element={<AddProduct />} />
            <Route path="/yonatan/edit-product/:id" element={<AddProduct />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Overlays />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
